/**
 * AK-584 — шаг 3 мастера: «Готово, вот ваша карта».
 *
 * Р1: третий шаг живёт не на отдельном экране, а прямо на карте — именно здесь
 * важно показать поле, а не рассказывать о нём в вакууме.
 *
 * Р4: честное ожидание вместо пустоты. Человек, нарисовавший первое поле,
 * видит серый контур и решает, что система сломана. Поэтому прямо говорим,
 * когда появится окраска по снимку и где её искать. Цифры — ожидание
 * (пролёты + облачность), а не замер, поэтому «обычно» и «примерно».
 */
import { CheckCircle2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { AddFieldPhase } from '@/lib/map/addField/addFieldStore';

/**
 * Показывать ли карточку «Готово».
 *
 * Правило вынесено из JSX, потому что ошибиться тут дорого: карточка занимает
 * ТОТ ЖЕ нижний слот карты (`bottom-4`, `z-40`), что и лист добавления поля и
 * пилюля «Ищу границы…». После сохранения режим остаётся включённым, и человек
 * обычно сразу тапает следующее поле, не нажимая «Добавить ещё поле» — без
 * гейта по фазе форма рисовалась бы поверх карточки (ревью head-web по PR
 * #746, блокер 2). Показываем только в покое: фаза `armed` и импорт не идёт.
 */
export function shouldShowFirstFieldDone(s: {
  onboardingRun: boolean;
  savedCount: number;
  dismissed: boolean;
  phase: AddFieldPhase;
  importPhase: string;
}): boolean {
  if (!s.onboardingRun || s.dismissed) return false;
  if (s.savedCount < 1) return false;
  if (s.phase !== 'armed') return false;
  return s.importPhase === 'idle';
}

interface FirstFieldDoneCardProps {
  /** Сколько полей добавлено за этот заход (обычно 1). */
  savedCount: number;
  /** «Добавить ещё поле» — просто убрать карточку, режим остаётся включённым. */
  onAddMore: () => void;
  /** «Перейти на карту» — убрать карточку и выйти из режима добавления. */
  onFinish: () => void;
}

export function FirstFieldDoneCard({
  savedCount,
  onAddMore,
  onFinish,
}: FirstFieldDoneCardProps) {
  return (
    <div
      data-testid="first-field-done-card"
      className="absolute bottom-4 left-1/2 z-40 w-[min(420px,calc(100%-32px))] -translate-x-1/2 rounded-xl border border-border bg-popover p-4 text-popover-foreground shadow-2xl"
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <CheckCircle2 size={16} className="text-primary" />
          {savedCount > 1
            ? `Полей добавлено: ${savedCount}`
            : 'Первое поле добавлено'}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="-mr-1 -mt-1 h-7 w-7"
          aria-label="Закрыть"
          onClick={onFinish}
        >
          <X size={16} />
        </Button>
      </div>

      <ul className="mb-3 flex flex-col gap-2 text-xs leading-snug text-muted-foreground">
        <li>
          <span className="font-medium text-foreground">
            Снимки со спутника
          </span>{' '}
          подтянутся сами. Первый снимок обычно появляется в течение суток,
          дальше — примерно раз в 3–5 дней в ясную погоду.
        </li>
        <li>
          Состояние посевов смотрите на карте: слой{' '}
          <span className="font-medium text-foreground">«Вегетация»</span>{' '}
          раскрашивает поле по снимку, история — в карточке поля.
        </li>
      </ul>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          className="flex-1"
          data-testid="first-field-done-finish"
          onClick={onFinish}
        >
          Перейти на карту
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="flex-1"
          data-testid="first-field-done-add-more"
          onClick={onAddMore}
        >
          Добавить ещё поле
        </Button>
      </div>
    </div>
  );
}

export default FirstFieldDoneCard;
